import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  provideNativeDateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, Subscription } from 'rxjs';
import { NewsService } from '../../../shared/services/news/news.service';
import { UsersService } from '../../../shared/services/users/users.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { UploadFilesComponent } from '../../../shared/components/upload-files/upload-files.component';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { CustomPipe } from '../../../shared/pipes/custom.pipe';
import { MailTitlePipe } from '../../../shared/pipes/mail-title.pipe';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';

// Define custom date formats
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-incorporation-form',
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatSortModule,
    MatTableModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatPaginatorModule,
    MatDialogModule,
    MatMenuModule,
    MatTabsModule,
    QuillModule,
    LoaderComponent,
    UploadFilesComponent,
    TitleComponent,
    CustomPipe,
    MailTitlePipe,
    QeqPipe,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './incorporation-form.component.html',
  styleUrls: [
    './incorporation-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class IncorporationFormComponent implements OnDestroy {
  // List view properties
  title: string = 'Incorporaciones';
  subtitle: string = 'Gestiona las incorporaciones de los empleados.';

  // Form view properties
  modules: any;
  myroles: any = {};

  // Common properties
  element: any = null;
  incorporationForm: FormGroup;
  toppingList: string[] = ['Incorporación', 'Desvinculación', 'Cambios'];

  // List view specific properties
  toppings = new FormControl<number[]>([]);
  statusMap: { [key: string]: string } = {
    Incorporación: 'Incorporación',
    Desvinculación: 'Desvinculación',
    Cambios: 'Cambios',
  };

  programmed = new FormControl<number[]>([]);
  programmedList: string[] = ['Programada', 'No programada'];
  programmedMap: { [key: string]: boolean } = {
    Programada: true,
    'No programada': false,
  };

  selectedFilter: string = 'todo';
  filters = [
    { value: 'mes', label: 'Mes' },
    { value: 'semana', label: 'Últimos 7 días' },
    { value: 'hoy', label: 'Hoy' },
    { value: 'todo', label: 'Todo' },
  ];

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  displayedColumns: string[] = [
    'title',
    'author',
    'id',
    'type',
    'published-date',
    'notifications',
    'acciones',
  ];

  // Form view specific properties
  selectedFiles: File[] = [];
  selectedImage: File | null = null;
  isProgrammed: boolean = false;
  notifications: boolean = false;
  imageRemoved: boolean = false;
  dateValue: Date | null = null;
  timeValue: string = '00:00';

  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];
  private navigationSubscription: Subscription | null = null;

  // View state management
  isUuid: boolean = false;
  showAddForm: boolean = false;
  isEditMode: boolean = false;
  editingId: number | null = null;

  @ViewChild(UploadFilesComponent) uploadFilesComponent!: UploadFilesComponent;
  @ViewChild(MatSort) sort: MatSort = new MatSort();
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private _liveAnnouncer = inject(LiveAnnouncer);
  private _snackBar = inject(MatSnackBar);
  readonly dialog = inject(MatDialog);

  get isLoading() {
    return this.loaderService.getLoader();
  }

  constructor(
    private usersService: UsersService,
    private newsService: NewsService,
    private router: Router,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private loaderService: LoaderService,
    private quillConfigService: QuillConfigService,
    private rolesService: RolesService,
    private storageService: StorageService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();

    this.incorporationForm = this.fb.group({
      title: ['', [Validators.required]],
      content: ['', [Validators.required]],
      toppings: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    this.loaderService.showLoader();
    this.setupNavigationListener();
    this.loadRoles();
    this.loadIncorporationData();
    this.setupFilterSubscriptions();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.setupCustomSorting();
  }

  ngOnDestroy() {
    this.clearSubscriptions();
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
  }

  // Form view methods
  openSnackBar(message: string) {
    this._snackBar.open(message, 'Cerrar', {
      duration: 5000,
    });
  }

  get f() {
    return this.incorporationForm.controls;
  }

  getFormattedDateTime(): string {
    if (!this.dateValue) return 'Fecha no seleccionada';
    return `${this.dateValue.toLocaleDateString()} ${this.timeValue}`;
  }

  getCombinedDateTime(): Date | null {
    if (!this.dateValue) return null;

    const [hours, minutes] = this.timeValue.split(':').map(Number);
    const year = this.dateValue.getFullYear();
    const month = this.dateValue.getMonth();
    const day = this.dateValue.getDate();
    // Create a local Date (interpreted in the user's timezone) then later call toISOString()
    // so that the resulting string reflects the true UTC time instead of a fake local+Z.
    return new Date(year, month, day, hours, minutes, 0, 0);
  }

  onFileSelected(files: File[]): void {
    this.selectedFiles = files;
    if (files && files.length > 0) {
      this.selectedImage = files[0];
      this.imageRemoved = false;
    }
  }

  addIncorporate() {
    this.showAddForm = true;
    this.isEditMode = false;
    this.resetForm();
  }

  editIncorporate(element: any) {
    this.showAddForm = true;
    this.isEditMode = true;
    this.editingId = element.id;
    this.element = element;
    this.imageRemoved = false;
    this.selectedImage = null;
    this.loadIncorporateData(element);
  }

  private resetForm() {
    this.incorporationForm.reset();
    this.selectedFiles = [];
    this.selectedImage = null;
    this.isProgrammed = false;
    this.notifications = false;
    this.dateValue = null;
    this.timeValue = '00:00';
  }

  private loadIncorporateData(element: any) {
    this.incorporationForm.patchValue({
      title: element.title,
      content: element.content,
      toppings: element.addition_type,
    });
    this.notifications = element.notifications;
    this.isProgrammed = element.programmed;
    if (element.publication_date) {
      const pubDate = new Date(element.publication_date);
      this.dateValue = pubDate;
      this.timeValue = pubDate.toTimeString().substring(0, 5);
    }
  }

  cancelForm() {
    this.showAddForm = false;
    this.isEditMode = false;
    this.editingId = null;
    this.resetForm();
  }

  saveChanges() {
    this.loaderService.showLoader();
    if (this.incorporationForm.invalid) {
      this.incorporationForm.markAllAsTouched();
      this.openSnackBar('Por favor, complete todos los campos obligatorios.');
      this.loaderService.hideLoader();
      return;
    }

    const formData = new FormData();

    // Get content with special handling for emojis
    let content = this.incorporationForm.get('content')?.value;

    // Try to get the actual HTML content from the Quill editor to preserve emojis
    try {
      const quillEditor = document.querySelector('.ql-editor') as HTMLElement;
      if (quillEditor && quillEditor.innerHTML) {
        const htmlContent = quillEditor.innerHTML;

        // Always use the HTML content from the editor as it preserves emojis better
        //  console.log('Using HTML content from Quill editor to preserve emojis');
        content = htmlContent;
      }
    } catch (error) {
      //console.log('Could not extract HTML content, using form value');
    }

    formData.append('title', this.incorporationForm.get('title')?.value);
    if (content) {
      formData.append('content', content);
      //console.log('Saving incorporation with content:', content);
    }
    formData.append(
      'addition_type',
      this.incorporationForm.get('toppings')?.value
    );
    formData.append('notifications', this.notifications ? 'true' : 'false');
    formData.append('programmed', this.isProgrammed ? 'true' : 'false');

    if (this.selectedFiles.length > 0) {
      // Prefer selectedImage (single-file mode) when available
      if (this.selectedImage) {
        formData.append('image', this.selectedImage);
      } else {
        formData.append('image', this.selectedFiles[0]);
      }
    }

    // If image was removed while editing, tell the backend to remove it
    if (this.isEditMode && this.imageRemoved) {
      formData.append('remove_image', 'true');
    }

    if (this.isProgrammed) {
      const combinedDateTime = this.getCombinedDateTime();
      if (combinedDateTime) {
        formData.append('publication_date', combinedDateTime.toISOString());
      }
    } else {
      formData.append('publication_date', new Date().toISOString());
    }

    const operation = this.isEditMode
      ? this.newsService.putTeamChanges(this.editingId!, formData)
      : this.newsService.postTeamChanges(formData);

    operation.subscribe({
      next: (result: any) => {
        this.loaderService.hideLoader();
        const message = this.isEditMode
          ? 'Incorporación actualizada con éxito.'
          : 'Incorporación creada con éxito.';
        this.openSnackBar(message);
        this.cancelForm();
        this.loadIncorporationData();
      },
      error: (error: any) => {
        this.loaderService.hideLoader();
        console.error('Error:', error);
        const message = this.isEditMode
          ? 'Error al actualizar la incorporación.'
          : 'Error al crear la incorporación.';
        this.openSnackBar(message);
      },
    });
  }

  // List view methods
  setupNavigationListener() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.cdr.detectChanges();
      });
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'incorporations',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;
      this.rolesService.checkWritePermission(
        'incorporations',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/communication/incorporation',
        'No tienes permisos para añadir una incorporación'
      );
    } else {
      // console.log('No user roles found in storage');
    }
  }

  loadIncorporationData() {
    const additionsSub = this.newsService.getTeamChanges().subscribe({
      next: (data) => {
        let filteredAdditions = data.sort((a, b) => {
          const dateA = a.publication_date
            ? new Date(a.publication_date).getTime()
            : new Date(a.created_when).getTime();
          const dateB = b.publication_date
            ? new Date(b.publication_date).getTime()
            : new Date(b.created_when).getTime();
          return dateB - dateA;
        });
        this.dataSource = new MatTableDataSource(filteredAdditions);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        this.setupCustomSorting();
        this.setupFilterPredicate();
        this.loaderService.hideLoader();
      },
      error: (error: any) => {
        console.error('There was an error!', error);
        this.loaderService.hideLoader();
      },
    });

    this.subscriptions.push(additionsSub);
  }

  selectFilter(filter: string) {
    this.selectedFilter = filter;
    this.applyAllFilters();
  }

  private getStartDateForFilter(filter: string): Date | null {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (filter) {
      case 'mes':
        const monthStart = new Date(today);
        monthStart.setDate(1);
        return monthStart;
      case 'semana':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 6);
        return weekStart;
      case 'hoy':
        return today;
      case 'todo':
      default:
        return null;
    }
  }

  applyAllFilters() {
    const statusValues = this.getStatusFilterValues();
    const programmedValues = this.getProgrammedFilterValues();
    const startDate = this.getStartDateForFilter(this.selectedFilter);

    const filterValue = JSON.stringify({
      statusFilter: statusValues,
      programmedFilter: programmedValues,
      dateFilter: startDate ? startDate.getTime() : null,
    });

    this.dataSource.filter = filterValue;
  }

  private getStatusFilterValues(): string[] {
    const selectedStatuses = this.toppings.value;
    return (
      selectedStatuses?.map((statusName) => this.statusMap[statusName]) || []
    );
  }

  private getProgrammedFilterValues(): boolean[] {
    const selectedProgrammed = this.programmed.value;
    return (
      selectedProgrammed?.map((progName) => this.programmedMap[progName]) || []
    );
  }

  setupFilterSubscriptions() {
    const toppingsSub = this.toppings.valueChanges.subscribe(() => {
      this.applyAllFilters();
    });
    this.subscriptions.push(toppingsSub);

    const programmedSub = this.programmed.valueChanges.subscribe(() => {
      this.applyAllFilters();
    });
    this.subscriptions.push(programmedSub);
  }

  setupFilterPredicate() {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterObj = JSON.parse(filter);
      let statusMatch = true;
      let programmedMatch = true;
      let dateMatch = true;

      if (filterObj.statusFilter && filterObj.statusFilter.length > 0) {
        statusMatch = filterObj.statusFilter.includes(data.addition_type);
      }

      if (filterObj.programmedFilter && filterObj.programmedFilter.length > 0) {
        programmedMatch = filterObj.programmedFilter.includes(data.programmed);
      }

      if (filterObj.dateFilter) {
        const createdWhen = new Date(data.created_when).getTime();
        dateMatch = createdWhen >= filterObj.dateFilter;
      }

      return statusMatch && programmedMatch && dateMatch;
    };
  }

  private setupCustomSorting() {
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'type':
          const typeOrder: { [key: string]: number } = {
            Incorporación: 0,
            Cambios: 1,
            Desvinculación: 2,
          };
          return typeOrder[item.addition_type] !== undefined
            ? typeOrder[item.addition_type]
            : 999;
        case 'published-date':
          return new Date(item.publication_date).getTime();
        case 'notifications':
          return item.notifications ? 0 : 1;
        default:
          return item[property];
      }
    };
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  goToMail(element: any) {
    this.editIncorporate(element);
  }

  deleteSelected(id: number, nick: string) {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quieres eliminar esta incorporación?',
        message: nick,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          return this.newsService.deleteTeamChanges(id);
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.dataSource.data = this.dataSource.data.filter(
          (user) => user.id !== id
        );
      }
    });
  }

  clearSubscriptions() {
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

  applyFilters(
    selectedStatusValues: string[],
    selectedProgrammedValues: boolean[]
  ) {
    this.applyAllFilters();
  }

  applyStatusFilter(selectedStatusValues: string[]) {
    this.applyAllFilters();
  }

  removeImage(): void {
    if (this.element) {
      this.element.image_url = null;
    }
    this.imageRemoved = true;
  }
}
